import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useIncidents } from "@/contexts/IncidentsContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Radio, Send, PlusCircle, AlertTriangle } from "lucide-react";

const empty = {
  call_type: "", address: "", city: "", state: "", zip_code: "",
  notes: "", priority: "medium", branch: "POLICE", box_number: "",
  lat: "", lon: "",
};

export default function InlineCallEntry({ onCreated, mapCenter, prefill }) {
  const { createIncident, updateIncident, incidents } = useIncidents();
  const { callTypes, branches, priorities } = useSettings();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  // If duplicate open incidents are found at the same address, we stash them
  // here and pop a confirmation dialog before actually creating. The user then
  // picks: "same call — add my notes as an update", "separate emergency —
  // create anyway", or cancel.
  const [dupCheck, setDupCheck] = useState(null); // { matches: [...], pendingPayload }
  const debounceRef = useRef(null);
  // Bias geocoding toward the current map viewport so nearby streets rank first.
  const nearHint = Array.isArray(mapCenter) && mapCenter.length === 2
    ? `${mapCenter[0]},${mapCenter[1]}`
    : null;

  // When the dispatcher drops a pin on the map, `prefill` gets a fresh
  // timestamp — flip that into the address / city / state / zip / lat / lon
  // fields so the form is ready to send. We keep call_type, branch, priority,
  // notes, and box_number untouched so the dispatcher can pick them next.
  const lastPrefillStamp = useRef(null);
  useEffect(() => {
    if (!prefill || !prefill._stamp) return;
    if (lastPrefillStamp.current === prefill._stamp) return;
    lastPrefillStamp.current = prefill._stamp;
    setForm((f) => ({
      ...f,
      address: prefill.address || f.address,
      city: prefill.city || f.city,
      state: prefill.state || f.state,
      zip_code: prefill.zip_code || f.zip_code,
      lat: prefill.lat || f.lat,
      lon: prefill.lon || f.lon,
    }));
    // Close any stale search dropdown so the pin-drop doesn't compete with typed suggestions.
    setSuggestions([]);
    setShowSuggestions(false);
  }, [prefill]);

  const setF = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "call_type") {
        // Match by label OR id so both the visible label and internal id resolve.
        const ct = callTypes.find((c) => c.label === v || c.id === v);
        if (ct?.branch) next.branch = ct.branch;
        // Auto-fill priority when the call type has one defined (Excel import
        // or admin-set). Match against known priority ids so a stray value
        // from an old row doesn't reset the dropdown.
        if (ct?.priority) {
          const pid = String(ct.priority).toLowerCase();
          if (priorities.some((p) => p.id === pid)) next.priority = pid;
        }
      }
      return next;
    });
  };

  // Predictive geocoding via Nominatim autocomplete (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!form.address || form.address.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = { q: form.address, limit: 6 };
        if (nearHint) params.near = nearHint;
        const r = await api.get(`/geocode/search`, { params });
        setSuggestions(r.data || []);
      } catch { setSuggestions([]); }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.address, nearHint]);

  // Manual "search now" — bypasses debounce and auto-picks the top result.
  // Used when autocomplete didn't populate or the user just wants to force a lookup.
  const runManualSearch = async () => {
    const q = (form.address || "").trim();
    if (q.length < 3) {
      toast.error("Enter at least 3 characters to search");
      return;
    }
    setSearching(true);
    try {
      const params = { q, limit: 6 };
      if (nearHint) params.near = nearHint;
      const r = await api.get(`/geocode/search`, { params });
      const results = r.data || [];
      if (!results.length) {
        toast.error(`No match for "${q}"`);
        setSuggestions([]);
        return;
      }
      setSuggestions(results);
      // Auto-pick the top result so all fields fill in instantly.
      pickSuggestion(results[0]);
      const label = results[0].is_intersection ? "Intersection" : "Address";
      toast.success(`${label} located: ${results[0].address_street}`);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const pickSuggestion = (s) => {
    // Backend now returns a normalized shape: address_street / city / state /
    // zip_code / lat / lon / is_intersection / poi_label. Business names are
    // already stripped server-side — we just pass the physical street through.
    const street = (s.address_street || "").trim();
    const city = (s.city || "").trim();
    const state = (s.state || "").toUpperCase().slice(0, 2);
    const zip = (s.zip_code || "").trim();
    setForm((f) => ({
      ...f,
      address: street || f.address,
      city: city || f.city,
      state: state.length === 2 ? state : f.state,
      zip_code: zip || f.zip_code,
      lat: parseFloat(s.lat),
      lon: parseFloat(s.lon),
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const normAddr = (s) => (s || "").toLowerCase().replace(/[.,#]+/g, " ").replace(/\s+/g, " ").trim();
  // Find any OPEN (non-resolved / non-cancelled) incidents at the same street
  // address and city. Match is fuzzy on address (case- and punctuation-
  // insensitive) so "12 Main St." and "12 main street" collapse together.
  const findDuplicates = (payload) => {
    const addr = normAddr(payload.address);
    const city = normAddr(payload.city);
    if (!addr || !city) return [];
    return (incidents || []).filter((i) => {
      if (i.status && (i.status === "resolved" || i.status === "cancelled")) return false;
      return normAddr(i.address) === addr && normAddr(i.city) === city;
    });
  };

  const finaliseCreate = async (payload) => {
    setSubmitting(true);
    try {
      const created = await createIncident(payload);
      toast.success(`${created.incident_number} dispatched & paged`);
      setForm(empty);
      onCreated?.(created);
    } catch { toast.error("Failed to create incident"); }
    finally { setSubmitting(false); setDupCheck(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lon: form.lon ? parseFloat(form.lon) : null,
      };
      if (!payload.lat && form.address && form.city) {
        try {
          const q = `${form.address}, ${form.city}, ${form.state} ${form.zip_code}`;
          const params = { q, limit: 1 };
          if (nearHint) params.near = nearHint;
          const r = await api.get(`/geocode/search`, { params });
          const data = r.data;
          if (data?.[0]) { payload.lat = parseFloat(data[0].lat); payload.lon = parseFloat(data[0].lon); }
        } catch { /* geocode failed — proceed without coords */ }
      }
      const matches = findDuplicates(payload);
      if (matches.length > 0) {
        // Stop here; let the user resolve the ambiguity via the dialog.
        setDupCheck({ matches, pendingPayload: payload });
        setSubmitting(false);
        return;
      }
      await finaliseCreate(payload);
    } catch { toast.error("Failed to create incident"); setSubmitting(false); }
  };

  // Actions from the duplicate-confirmation dialog
  const dupTreatAsSame = async (existingId) => {
    const p = dupCheck?.pendingPayload;
    if (!p) return;
    setSubmitting(true);
    try {
      const updateMsg = (p.notes || "").trim() || "Duplicate call reported";
      await updateIncident(existingId, {
        update_message: updateMsg,
        repage: true,
      });
      toast.success("Added as update to existing call");
      setForm(empty);
    } catch { toast.error("Failed to update existing call"); }
    finally { setSubmitting(false); setDupCheck(null); }
  };
  const dupCreateAnyway = async () => {
    const p = dupCheck?.pendingPayload;
    if (!p) return;
    await finaliseCreate(p);
  };

  return (
    <form onSubmit={submit} className="h-full flex flex-col" data-testid="inline-call-entry">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between bg-card/80">
        <div className="font-mono text-xs tracking-widest text-primary flex items-center gap-2">
          <Radio className="w-3 h-3" /> // CALL ENTRY
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">{new Date().toLocaleTimeString()}</div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Branch *</Label>
            <Select value={form.branch} onValueChange={(v) => setF("branch", v)}>
              <SelectTrigger className="h-9" data-testid="branch-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`branch-opt-${b.id}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                      {b.id}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Priority *</Label>
            <Select value={form.priority} onValueChange={(v) => setF("priority", v)}>
              <SelectTrigger className="h-9" data-testid="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`priority-opt-${p.id}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                      {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Call Type *</Label>
          <Select value={form.call_type} onValueChange={(v) => setF("call_type", v)} required>
            <SelectTrigger data-testid="call-type-select" className="h-9"><SelectValue placeholder="Select call type" /></SelectTrigger>
            <SelectContent>
              {callTypes.map((c) => {
                const b = branches.find((x) => x.id === c.branch);
                return (
                  <SelectItem key={c.id} value={c.label}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: b?.color || "#666" }} />
                      {c.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Label className="text-xs">Incident Address *</Label>
          <div className="flex gap-1">
            <Input
              data-testid="address-input"
              value={form.address}
              onChange={(e) => { setF("address", e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={(e) => {
                // Enter triggers a manual search when no suggestion is highlighted.
                if (e.key === "Enter" && !suggestions.length) {
                  e.preventDefault();
                  runManualSearch();
                }
              }}
              placeholder="Type an address or intersection (Main & Broadway, Main/Broadway, Main and Broadway)"
              className="h-9 flex-1"
              required
              autoComplete="off"
            />
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9 shrink-0"
              onClick={runManualSearch}
              disabled={searching}
              title="Search this address / intersection"
              data-testid="address-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            </Button>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div
              data-testid="address-suggestions"
              className="absolute z-30 left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-xl max-h-64 overflow-y-auto"
            >
              {suggestions.map((s, i) => {
                const street = s.address_street || s.display_name.split(",")[0];
                const rest = [s.city, s.state, s.zip_code].filter(Boolean).join(", ");
                return (
                  <button
                    key={`${s.lat}-${s.lon}-${i}`}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-accent border-b border-border/40 last:border-0"
                    data-testid={`address-suggestion-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate flex-1">{street}</div>
                      {s.is_intersection && (
                        <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-500 border border-amber-500/40" data-testid={`intersection-badge-${i}`}>
                          ✕ INTERSECTION
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground truncate text-[10px]">
                      {rest || s.display_name}
                      {s.poi_label && <span className="ml-2 italic opacity-70">· {s.poi_label}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-xs">City *</Label>
            <Input data-testid="city-input" value={form.city} onChange={(e) => setF("city", e.target.value)} className="h-9" required />
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Input data-testid="state-input" value={form.state} onChange={(e) => setF("state", e.target.value.toUpperCase())} className="h-9 font-mono" maxLength={2} required />
          </div>
        </div>
        <div>
          <Label className="text-xs">ZIP *</Label>
          <Input data-testid="zip-input" value={form.zip_code} onChange={(e) => setF("zip_code", e.target.value)} className="h-9 font-mono" required />
        </div>

        {form.branch === "FIRE" && (
          <div>
            <Label className="text-xs">BOX Number</Label>
            <Input
              data-testid="box-number-input"
              value={form.box_number}
              onChange={(e) => setF("box_number", e.target.value)}
              className="h-9 font-mono"
              placeholder="e.g. 1234 (optional)"
            />
          </div>
        )}

        <div>
          <Label className="text-xs">Notes / Comments</Label>
          <Textarea
            data-testid="notes-input"
            rows={3}
            value={form.notes}
            onChange={(e) => setF("notes", e.target.value)}
            placeholder="Caller statements, hazards, weapons, victims…"
            className="resize-none"
          />
        </div>

        {(form.lat && form.lon) && (
          <div className="font-mono text-[10px] text-muted-foreground">
            📍 {parseFloat(form.lat).toFixed(5)}, {parseFloat(form.lon).toFixed(5)}
          </div>
        )}
      </div>

      <div className="border-t border-border p-3 flex gap-2 bg-card/80">
        <Button type="button" variant="outline" size="sm" onClick={() => setForm(empty)} className="flex-1" data-testid="clear-form-btn">
          Clear
        </Button>
        <Button type="submit" disabled={submitting} data-testid="send-incident-btn" className="flex-1 font-mono tracking-widest" size="sm">
          {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
          SEND →
        </Button>
      </div>
      <Dialog open={!!dupCheck} onOpenChange={(v) => { if (!v) setDupCheck(null); }}>
        <DialogContent className="max-w-lg" data-testid="dup-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="w-4 h-4" /> Duplicate call?
            </DialogTitle>
            <DialogDescription>
              There is already an active job at this address. Is this the same call, or a separate emergency?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(dupCheck?.matches || []).map((m) => (
              <div key={m.id} data-testid={`dup-match-${m.id}`} className="border border-border rounded-sm p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-xs text-primary">{m.incident_number}</div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">{m.branch} · {m.priority}</div>
                </div>
                <div className="font-semibold text-sm">{m.call_type}</div>
                <div className="text-xs text-muted-foreground">{m.address}, {m.city} {m.state} {m.zip_code}</div>
                {m.box_number && <div className="text-xs font-mono">BOX #{m.box_number}</div>}
                <div className="text-xs text-muted-foreground line-clamp-2">{m.notes || "—"}</div>
                <div className="text-[10px] font-mono text-muted-foreground">
                  paged {new Date(m.created_at).toLocaleString()} by {m.created_by_handle || m.created_by_name || "unknown"}
                </div>
                <div className="pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={submitting}
                    onClick={() => dupTreatAsSame(m.id)}
                    data-testid={`dup-treat-same-${m.id}`}
                    className="h-7 text-[11px]"
                  >
                    Same call — add my notes as an update
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDupCheck(null)} data-testid="dup-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              onClick={dupCreateAnyway}
              disabled={submitting}
              data-testid="dup-create-anyway-btn"
            >
              Separate emergency — create anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}
